import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import api from '../utils/api';

export interface UseAliIdReturn {
  aliId: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAliId = (): UseAliIdReturn => {
  const { isAuthenticated } = useAuth();
  const [aliId, setAliId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAliId = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      // Use dedicated endpoint that gets ALI ID for the current authenticated user
      const response = await api.get('/api/user/ali-id');

      if (response.data?.aliId) {
        setAliId(response.data.aliId);
      } else {
        setError('ALI ID not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ALI ID');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refetch = async () => {
    await fetchAliId();
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAliId();
    } else {
      setAliId(null);
      setError(null);
    }
  }, [isAuthenticated, fetchAliId]);

  return {
    aliId,
    loading,
    error,
    refetch,
  };
};